import React, { FC } from 'react';
import styled from 'styled-components';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { faComments, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useDropdown } from '../../../tools/hooks';
import { IState } from '../../../reducers';
import { IProfileReducer } from '../../../reducers/profileReducer';
import { colorStack } from '../../../styleHelpers/colors';
import { media } from '../../../styleHelpers/breakpoint';
import NotificationList from './NotificationList/NotificationList';
import { UserMenuDropdown } from './UserMenuDropdown';
import { Avatar } from '../../Common/Avatar/Avatar';
import { selectHeaderMessagingEnabled, selectHeaderNotificationsEnabled, selectHeaderSearchEnabled } from '../../../tools/platformConfiguration';
import { setConversationMenu } from '../../../actions/messagingActions';

const Wrapper = styled.div`
    padding-right: 1.5rem;
    margin-left: auto;
    ${media.tablet`
        width: 100%;
        display: flex;
    `}
`;

const RightMenu = styled.div`
    margin: 0 0 0 auto;
    display: flex;
    align-items: center;
    > div {
        margin: 0 0 0 1rem;
    }
`;

const MenuItem = styled.div`
    position: relative;
    cursor: pointer;
`;

const UserMenuBox = styled.div`
    position: relative;
    display: none;
    ${media.desktop`
        display: block;
    `}
`;

const HeaderIconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    outline: 0;
    cursor: pointer;
    border: 0;
    background: transparent;
    color: ${colorStack.darkGrey};
    padding: 0;
`;

const ImageBox = styled.button`
    display: flex;
    align-items: center;
    outline: 0;
    cursor: pointer;
    border: 0;
    svg {
        cursor: pointer;
        margin: 0 0 0 4px;
        color: ${colorStack.darkGrey};
    }
`;

export const DesktopNavMenu: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const { currentUserProfile } = useSelector<IState, IProfileReducer>(state => state.profile);
    const headerSearchEnabled = useSelector(selectHeaderSearchEnabled);
    const headerNotificationsEnabled = useSelector(selectHeaderNotificationsEnabled);
    const headerMessagingEnabled = useSelector(selectHeaderMessagingEnabled);
    const [wrapperRef, dropdownOpen, toggleDropdown, closeDropdown] = useDropdown();

    return (
        <>
            {!!currentUserProfile &&
                <Wrapper role="naviagation">
                    <RightMenu>
                        {headerSearchEnabled && (
                            <MenuItem>
                                <HeaderIconButton type="button" onClick={() => history('/directory')} data-lc="js-lc-header-search">
                                    <FontAwesomeIcon icon={faSearch} />
                                </HeaderIconButton>
                            </MenuItem>
                        )}
                        {headerMessagingEnabled && (
                            <MenuItem>
                                <HeaderIconButton
                                    type="button"
                                    onClick={() => dispatch(setConversationMenu(true, true))}
                                    data-lc="js-lc-header-messaging"
                                >
                                    <FontAwesomeIcon icon={faComments} />
                                </HeaderIconButton>
                            </MenuItem>
                        )}
                        {headerNotificationsEnabled && (
                            <MenuItem>
                                <NotificationList />
                            </MenuItem>
                        )}
                        <UserMenuBox ref={wrapperRef}>
                            <ImageBox onClick={toggleDropdown}>
                                <Avatar
                                    preventRedirection={true}
                                    picture={currentUserProfile?.picture}
                                    firstName={currentUserProfile.firstName}
                                    lastName={currentUserProfile.lastName}
                                    id={currentUserProfile.id}
                                />
                                <FontAwesomeIcon icon={faCaretDown} />
                            </ImageBox>
                            {dropdownOpen && (
                                <UserMenuDropdown currentUserProfile={currentUserProfile} closeDropdown={closeDropdown} />
                            )}
                        </UserMenuBox>
                    </RightMenu>
                </Wrapper>
            }
        </>
    );
};
